import { useEffect, useState } from "react";
import { api, getActiveBook } from "../api";
import { usd } from "../lib/money";
import { MoneyBadge } from "./MoneyScope";

// Commodities desk — Stage A of docs/COMMODITIES_PLAN.md: commodity exposure
// through Alpaca ETFs, no new venue. The page is organised the way the sleeve
// reasons: what it perceives (hard prints, positioning), what it may trade
// (the allow-list, with the decay tag that governs hold length), and what it
// currently holds.
//
// EXPOSURE-FIRST. Every row is named for the commodity, not the ticker, because
// the ticker is the part that changes when this moves to Ironbeam futures.

type Reading = {
  label: string; units: string; period: string; level: number; change: number;
  direction: string; five_year_normal?: number; vs_normal?: number; vs_normal_pct?: number;
};
type Release = { report: string; when: string; days_away: number; released_today: boolean };
type Cot = {
  contract: string; as_of: string; age_days: number; net: number; change: number;
  open_interest: number; net_pct_oi: number; percentile_3y: number | null; side: string;
};
type Universe = {
  exposure: string; vehicle: string; bear: string; growth_vehicle: string | null;
  decay: string; price: number | null; max_hold_days: number | null;
};
type Signals = {
  eia: { available: boolean; petroleum?: Record<string, Reading>; natgas?: Reading | null;
         next_releases?: Release[]; error?: string };
  cot: { contracts: Record<string, Cot>; stale: string[]; note?: string; error?: string };
  tape: Record<string, { close: number; chg_pct: number }>;
  universe: Universe[];
  banned_note: string;
};
type Holding = {
  symbol: string; exposure: string | null; decay: string | null; side: string;
  qty: number; entry: number; stop: number | null; target: number | null;
  age_days: number | null; rationale: string;
};
type Sleeve = {
  enabled: boolean; book: string; budget: number; deployed: number; headroom: number;
  alpaca_pct: number; shorting_allowed: boolean; shorting_note: string;
  min_ticket: number; holdings: Holding[];
};

const DECAY_TONE: Record<string, string> = {
  physical: "bg-accent/15 text-accent",
  roll: "bg-gold/15 text-gold",
};
const num = (n: number, d = 0) =>
  n.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
// "3th" is the tell that a second copy of this rule exists. The backend's text
// block already had an ordinal helper; this table rendered `${n}th` directly
// and produced 3th / 83rd side by side on the same page.
const ordinal = (n: number) =>
  11 <= n % 100 && n % 100 <= 13 ? `${n}th`
    : `${n}${ ({ 1: "st", 2: "nd", 3: "rd" } as Record<number, string>)[n % 10] ?? "th" }`;

export default function Commodities() {
  const book = getActiveBook();
  const [sleeve, setSleeve] = useState<Sleeve | null>(null);
  const [sig, setSig] = useState<Signals | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setSleeve(null);
    api.get<Sleeve>(`/api/commodities/sleeve?book=${book}`)
      .then((r) => { if (live) { setSleeve(r); setErr(null); } })
      .catch((e) => { if (live) setErr((e as Error).message); });
    api.get<Signals>(`/api/commodities/signals?book=${book}`)
      .then((r) => { if (live) setSig(r); })
      .catch(() => {});
    return () => { live = false; };
  }, [book]);

  if (err) return <div className="card text-down text-sm font-mono">commodities desk unavailable — {err}</div>;

  const pet = Object.values(sig?.eia?.petroleum ?? {});
  const gas = sig?.eia?.natgas ?? null;
  const prints = gas ? [...pet, gas] : pet;

  return (
    <div className="space-y-4">
      {/* ── State ────────────────────────────────────────────────────────── */}
      <div className="card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="label mb-0">
              Commodities <span className="normal-case text-slate-500">— Stage A: exposure through Alpaca ETFs</span>
            </h2>
            <p className="text-xs text-slate-500 mt-1 max-w-2xl">
              The firm holds <span className="text-slate-300">exposures</span>, not tickers — every row below is
              named for the commodity, because the ticker is the part that changes when this moves to
              Ironbeam futures.
            </p>
          </div>
          <MoneyBadge money={book.startsWith("live") ? "real" : "paper"} />
        </div>

        {sleeve && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
              <Stat label="Budget" value={usd(sleeve.budget)}
                    sub={`${sleeve.alpaca_pct}% carve-out of this book's Alpaca slice`} />
              <Stat label="Deployed" value={usd(sleeve.deployed)} />
              <Stat label="Headroom" value={usd(sleeve.headroom)} />
              <Stat label="State"
                    value={sleeve.enabled ? "Armed" : "Off"}
                    tone={sleeve.enabled ? "up" : undefined}
                    sub={sleeve.enabled ? `min ticket ${usd(sleeve.min_ticket)}` : "not enabled in this book"} />
            </div>
            {!sleeve.enabled && (
              <p className="mt-3 text-xs text-slate-400 border border-slate-800 rounded p-2">
                The sleeve is armed on the paper books only. Live is a deliberate separate flip —
                shipping the code does not arm real money.
              </p>
            )}
            <p className="mt-3 text-xs">
              <span className={sleeve.shorting_allowed ? "text-up" : "text-gold"}>
                {sleeve.shorting_allowed ? "Two-sided" : "Long-only"}
              </span>
              <span className="text-slate-500"> — {sleeve.shorting_note}. Asked of the broker each
              time, not read from a config flag.</span>
            </p>
          </>
        )}
      </div>

      {/* ── Positions ────────────────────────────────────────────────────── */}
      <div className="card">
        <h3 className="label">Open exposures</h3>
        {!sleeve?.holdings.length ? (
          <p className="text-sm text-slate-500">
            No commodity positions in this book. The sleeve does not trade on its own yet — nothing on
            the scheduler calls it, so entries are deliberate.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-mono tabular-nums">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-slate-500 text-right">
                  <th className="text-left font-normal py-1">Exposure</th>
                  <th className="text-left font-normal">Vehicle</th>
                  <th className="font-normal">Side</th>
                  <th className="font-normal">Qty</th>
                  <th className="font-normal">Entry</th>
                  <th className="font-normal">Stop</th>
                  <th className="font-normal">Target</th>
                  <th className="font-normal">Age</th>
                </tr>
              </thead>
              <tbody>
                {sleeve.holdings.map((h, i) => (
                  <tr key={i} className="border-t border-slate-800 text-right">
                    <td className="text-left py-1.5 text-slate-200 font-sans capitalize">{h.exposure ?? "—"}</td>
                    <td className="text-left text-slate-400">
                      {h.symbol}
                      {h.decay && (
                        <span className={`badge ml-2 text-[10px] ${DECAY_TONE[h.decay] ?? ""}`}>{h.decay}</span>
                      )}
                    </td>
                    <td className={h.side === "long" ? "text-up" : "text-down"}>{h.side}</td>
                    <td className="text-slate-300">{h.qty}</td>
                    <td className="text-slate-400">{usd(h.entry)}</td>
                    <td className="text-slate-500">{h.stop ? usd(h.stop) : "—"}</td>
                    <td className="text-slate-500">{h.target ? usd(h.target) : "—"}</td>
                    <td className="text-slate-400">{h.age_days === null ? "—" : `${h.age_days}d`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── The allow-list ───────────────────────────────────────────────── */}
      {sig && (
        <div className="card overflow-x-auto">
          <h3 className="label">Tradable exposures</h3>
          <p className="text-xs text-slate-500 mb-2">
            An <span className="text-slate-300">allow-list</span>, not a preference: a commodity ETF is
            shaped exactly like an ordinary share, so membership of this table is what makes it a
            commodity to the firm. Anything absent is simply not reachable.
          </p>
          <table className="w-full text-sm font-mono tabular-nums">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                <th className="text-left font-normal py-1">Exposure</th>
                <th className="text-left font-normal">Long</th>
                <th className="text-left font-normal">Bearish</th>
                <th className="text-right font-normal">Last</th>
                <th className="text-left font-normal pl-4">Structure</th>
                <th className="text-right font-normal">Max hold</th>
              </tr>
            </thead>
            <tbody>
              {sig.universe.map((u) => (
                <tr key={u.exposure} className="border-t border-slate-800">
                  <td className="py-1.5 text-slate-200 font-sans capitalize">
                    {u.exposure}
                    {u.growth_vehicle && (
                      <span className="text-slate-600 text-xs font-mono"> · growth holds {u.growth_vehicle}</span>
                    )}
                  </td>
                  <td className="text-slate-300">{u.vehicle}</td>
                  <td className="text-slate-400">
                    {u.bear === u.vehicle ? <span className="text-slate-600">short {u.vehicle}</span>
                                          : <>short {u.bear}</>}
                  </td>
                  <td className="text-right text-slate-300">{u.price ? usd(u.price) : "—"}</td>
                  <td className="pl-4">
                    <span className={`badge text-[10px] ${DECAY_TONE[u.decay] ?? ""}`}>{u.decay}</span>
                    <span className="text-slate-600 text-xs font-sans ml-2">
                      {u.decay === "roll" ? "bleeds on every futures roll" : "no roll decay"}
                    </span>
                  </td>
                  <td className="text-right text-slate-400">{u.max_hold_days ? `${u.max_hold_days}d` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[11px] text-slate-500 mt-2">{sig.banned_note}</p>
        </div>
      )}

      {/* ── EIA hard prints ──────────────────────────────────────────────── */}
      {sig && (
        <div className="card">
          <h3 className="label">EIA hard prints</h3>
          {!sig.eia.available ? (
            <p className="text-sm text-slate-500">No EIA key configured — the desk falls back to headlines.</p>
          ) : !prints.length ? (
            <p className="text-sm text-slate-500">No readings returned.</p>
          ) : (
            <>
              <p className="text-xs text-slate-500 mb-2">
                Scheduled releases, not headlines. A <span className="text-down">build</span> is supply
                arriving (bearish); a <span className="text-up">draw</span> is supply leaving (bullish).
                Inventories are strongly seasonal, so the level against the five-year average for the
                same week is the signal — the bare level is not.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm font-mono tabular-nums">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-slate-500 text-right">
                      <th className="text-left font-normal py-1">Series</th>
                      <th className="font-normal">Level</th>
                      <th className="font-normal">Weekly</th>
                      <th className="font-normal">vs 5-yr normal</th>
                      <th className="text-left font-normal pl-4">Week ending</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prints.map((r, i) => (
                      <tr key={i} className="border-t border-slate-800 text-right">
                        <td className="text-left py-1.5 text-slate-200 font-sans">{r.label}</td>
                        <td className="text-slate-300">{num(r.level)} <span className="text-slate-600 text-xs">{r.units}</span></td>
                        <td className={r.direction === "build" ? "text-down" : r.direction === "draw" ? "text-up" : "text-slate-500"}>
                          {r.direction} {num(Math.abs(r.change))}
                        </td>
                        <td className={r.vs_normal === undefined ? "text-slate-600"
                             : r.vs_normal > 0 ? "text-down" : "text-up"}>
                          {r.vs_normal === undefined ? "—"
                            : `${r.vs_normal > 0 ? "+" : "−"}${num(Math.abs(r.vs_normal))} (${Math.abs(r.vs_normal_pct ?? 0).toFixed(1)}%)`}
                        </td>
                        <td className="text-left pl-4 text-slate-500">{r.period}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!!sig.eia.next_releases?.length && (
                <p className="text-xs text-slate-500 mt-2">
                  Next:{" "}
                  {sig.eia.next_releases.map((r, i) => (
                    <span key={i}>
                      {i > 0 && " · "}
                      <span className="text-slate-400">{r.report.replace("EIA ", "")}</span>{" "}
                      {r.released_today ? "released today" : r.days_away === 0 ? "today" : `in ${r.days_away}d`}
                    </span>
                  ))}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Positioning ──────────────────────────────────────────────────── */}
      {sig && !!Object.keys(sig.cot.contracts ?? {}).length && (
        <div className="card overflow-x-auto">
          <h3 className="label">Speculative positioning</h3>
          <p className="text-xs text-slate-500 mb-2">
            What the money has already done about the physical balance. A bullish draw into a market
            already at a multi-year net long is a different trade from the same draw into flat
            positioning — that is squeeze risk, and it is invisible in price alone.
          </p>
          <table className="w-full text-sm font-mono tabular-nums">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-500 text-right">
                <th className="text-left font-normal py-1">Contract</th>
                <th className="font-normal">Net</th>
                <th className="font-normal">% of OI</th>
                <th className="font-normal">Weekly</th>
                <th className="font-normal">3-yr pctile</th>
                <th className="text-left font-normal pl-4">As of</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(sig.cot.contracts).map(([k, c]) => (
                <tr key={k} className="border-t border-slate-800 text-right">
                  <td className="text-left py-1.5 text-slate-200 font-sans">{k}</td>
                  <td className={c.net >= 0 ? "text-up" : "text-down"}>{c.side} {num(Math.abs(c.net))}</td>
                  <td className="text-slate-400">{Math.abs(c.net_pct_oi).toFixed(1)}%</td>
                  <td className={c.change >= 0 ? "text-up" : "text-down"}>
                    {c.change >= 0 ? "+" : "−"}{num(Math.abs(c.change))}
                  </td>
                  <td className={c.percentile_3y === null ? "text-slate-600"
                       : c.percentile_3y >= 90 || c.percentile_3y <= 10 ? "text-gold" : "text-slate-400"}>
                    {c.percentile_3y === null ? "—" : ordinal(c.percentile_3y)}
                    {c.percentile_3y !== null && c.percentile_3y >= 90 && (
                      <span className="text-[10px] font-sans"> crowded</span>
                    )}
                    {c.percentile_3y !== null && c.percentile_3y <= 10 && (
                      <span className="text-[10px] font-sans"> washed out</span>
                    )}
                  </td>
                  <td className="text-left pl-4 text-slate-500">{c.as_of}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!!sig.cot.stale?.length && (
            <p className="text-[11px] text-slate-500 mt-2">
              Not shown (the series went stale at the CFTC, not an outage): {sig.cot.stale.join(", ")}
            </p>
          )}
          {sig.cot.note && <p className="text-[11px] text-slate-500 mt-1">{sig.cot.note}</p>}
        </div>
      )}

      {!sleeve && !err && <div className="card text-sm text-slate-500 font-mono">loading commodities desk…</div>}
    </div>
  );
}

function Stat({ label, value, sub, tone }: {
  label: string; value: string; sub?: string; tone?: "up" | "down";
}) {
  return (
    <div>
      <div className="label">{label}</div>
      <div className={`text-xl font-mono font-semibold tabular-nums ${
        tone === "up" ? "text-up" : tone === "down" ? "text-down" : "text-slate-100"}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}
